import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserContextType } from '../types';
import { hasQuizAccess as userHasQuizAccess } from '../utils/quizAccess';
import { login as apiLogin, logout as apiLogout, register as apiRegister, getProfile } from '../services/authService';
import { changePassword as apiChangePassword, updateEmailPreferences as apiUpdateEmailPreferences, ChangePasswordData } from '../services/accountService';

// Create a context with undefined as default value
const UserContext = createContext<UserContextType | undefined>(undefined);

/**
 * Provider component that wraps the application to make user data and
 * authentication functions available throughout the component tree.
 * 
 * This component:
 * - Maintains the current user state using React's useState hook
 * - Provides login/logout functionality to update the user state
 * - Offers helper methods to check user roles (isTeacher, isStudent)
 * - Makes all these functions and data available to child components via context
 * 
 * Typically used at a high level in the component tree to provide user
 * authentication state to the entire application.
 */
export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [authModalState, setAuthModalState] = useState<{ isOpen: boolean, message: string | undefined, title: string | undefined }>({
        isOpen: false,
        message: undefined,
        title: undefined
    });
    const [signupModalTrigger, setSignupModalTrigger] = useState<number>(0);

    // Function to check authentication status on load
    const checkAuthStatus = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    const user = await getProfile();
                    setCurrentUser(user);
                } catch (err) {
                    // Clear token if profile fetch fails (token might be invalid)
                    localStorage.removeItem('token');
                    setCurrentUser(null);
                }
            } else {
                setCurrentUser(null);
            }
        } catch (err) {
            console.error("Error during authentication check:", err);
            setError('Failed to check authentication status');
            setCurrentUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    // Run auth check on component mount
    useEffect(() => {
        checkAuthStatus();
    }, []);

    /**
     * Logs in a user using the API service.
     */
    const login = async (email: string, password: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const { user } = await apiLogin(email, password);
            setCurrentUser(user);
        } catch (err: any) {
            console.error("Login failed:", err);
            setError(err.message || 'Login failed');
            setCurrentUser(null); // Ensure user is null on error
            // Re-throw the error so the calling component can handle it (e.g., show message)
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Registers a new user using the API service.
     */
    const register = async (userData: { firstName: string; lastName: string; email: string; password: string; role: string }) => {
        setIsLoading(true);
        setError(null);
        try {
            const { user } = await apiRegister(userData);
            setCurrentUser(user);
        } catch (err: any) {
            console.error("Registration failed:", err);
            setError(err.message || 'Registration failed');
            setCurrentUser(null); // Ensure user is null on error
            // Re-throw the error so the calling component can handle it
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Clears the current user state and calls API logout
     */
    const logout = () => {
        setIsLoading(true); // Optional: set loading during logout
        apiLogout(); // Call the service function to remove token
        setCurrentUser(null);
        setError(null);
        setIsLoading(false); // Optional: clear loading
    };

    /**
     * Checks if the current user has a teacher role
     * @returns boolean indicating if user is a teacher
     */
    const isTeacher = () => {
        return currentUser?.role === 'teacher';
    };

    /**
     * Checks if the current user has a student role
     * @returns boolean indicating if user is a student
     */
    const isStudent = () => {
        return currentUser?.role === 'student';
    };

    /**
     * Checks if the user has active subscription or valid trial
     * @returns boolean indicating if user has quiz access
     */
    const hasQuizAccess = () => userHasQuizAccess(currentUser);

    /**
     * Checks if the user needs to subscribe
     * @returns boolean indicating if subscription is needed
     */
    const needsSubscription = () => {
        if (!currentUser) return false;
        return !hasQuizAccess();
    };

    /**
     * Shows the auth modal with optional custom message and title
     */
    const showAuthModal = (message?: string, title?: string) => {
        setAuthModalState({
            isOpen: true,
            message,
            title
        });
    };

    /**
     * Hides the auth modal
     */
    const hideAuthModal = () => {
        setAuthModalState({
            isOpen: false,
            message: undefined,
            title: undefined
        });
    };

    /**
     * Changes the user's password
     */
    const changePassword = async (currentPassword: string, newPassword: string, confirmPassword: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const passwordData: ChangePasswordData = {
                currentPassword,
                newPassword,
                confirmPassword
            };
            await apiChangePassword(passwordData);
        } catch (err: any) {
            console.error("Password change failed:", err);
            setError(err.message || 'Password change failed');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Updates the user's email preferences
     */
    const updateEmailPreferences = async (preferences: { all: boolean }) => {
        setIsLoading(true);
        setError(null);
        try {
            await apiUpdateEmailPreferences(preferences);
        } catch (err: any) {
            console.error("Email preferences update failed:", err);
            setError(err.message || 'Email preferences update failed');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Refreshes user data from the server
     */
    const refreshUserData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const user = await getProfile();
            setCurrentUser(user);
        } catch (err: any) {
            console.error("Failed to refresh user data:", err);
            setError(err.message || 'Failed to refresh user data');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Opens the signup modal and tracks the event with analytics
     * Triggers Meta Pixel and GA4 tracking for lead generation
     */
    const openSignupModal = () => {
        // Increment the trigger to notify MainLayout to open the signup modal
        setSignupModalTrigger(prev => prev + 1);

        // Track with Meta Pixel (Facebook Ads)
        if (typeof window !== 'undefined' && (window as any).fbq) {
            (window as any).fbq('track', 'Lead');
        }

        // Track with Google Analytics 4
        if (typeof window !== 'undefined' && (window as any).gtag) {
            (window as any).gtag('event', 'signup_modal_opened', {
                event_category: 'engagement',
                event_label: 'signup_modal'
            });
        }
    };

    return (
        <UserContext.Provider value={{
            currentUser,
            isLoading,
            error,
            login,
            register,
            logout,
            isTeacher,
            isStudent,
            hasQuizAccess,
            needsSubscription,
            checkAuthStatus,
            showAuthModal,
            hideAuthModal,
            authModalState,
            openSignupModal,
            signupModalTrigger,
            changePassword,
            updateEmailPreferences,
            refreshUserData
        }}>
            {children}
        </UserContext.Provider>
    );
};

/**
 * Custom hook that provides access to the UserContext.
 * Must be used within a UserProvider component.
 * Throws an error if used outside of UserProvider.
 * @returns The UserContext value containing user data and authentication methods
 */
export const useUser = (): UserContextType => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};


